import React, { memo, useMemo } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Typography } from '@mui/material';
import classnames from 'classnames';
import { DiaryEntrySyncStatus, DiaryTaskNameFormat } from 'graphql/types';
import { useMessages } from 'modules/intl';
import { SmartDiaryEntry } from '../type';
import { useIsMarkedToDelete } from './SmartListItemText';
import { ShowSeprator } from './ShowSeprator';

type CustomCPTLabelProps = {
  smartEntry: SmartDiaryEntry;
};

type StylesProps = {
  isEntryMarkedToDelete: boolean;
  hasStartEndTime: boolean;
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: ({ isEntryMarkedToDelete, hasStartEndTime }: StylesProps) => {
      if (isEntryMarkedToDelete && hasStartEndTime) return '50%';
      else if (isEntryMarkedToDelete) {
        return '62%';
      } else if (hasStartEndTime) {
        return '70%';
      } else {
        return '85%';
      }
    },
    padding: theme.spacing(0, 1, 0, 0.5)
  },
  line: {
    display: 'flex'
  },
  notSelected: {
    color: theme.palette.grey[500],
    padding: theme.spacing(0, 1, 0, 0.5),
    alignItems: 'center'
  },
  singleLineText: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden'
  },
  bold: {
    fontWeight: theme.typography.fontWeightBold
  },
  italic: {
    fontStyle: 'italic',
    paddingRight: theme.spacing(0.5),
    color: theme.palette.text.secondary
  },
  strikeThrough: {
    textDecoration: 'line-through'
  },
  primaryRow: {
    color: theme.palette.text.primary,
    ...theme.typography.body1
  },
  secondaryRow: {
    color: theme.palette.text.secondary,
    ...theme.typography.caption
  }
}));

const labelMessages = [
  'smartDiary.noProject',
  'smartDiary.noProgram',
  'smartDiary.noTask',
  'smartDiary.noCPTClient',
  'smartDiary.noCPTProject',
  'smartDiary.noCPTTask',
  'smartDiary.noClient',
  'smartDiary.no',
  'smartDiary.selected',
  'smartDiary.noCPTProgram'
];

type RowEntry = {
  text: string;
  id: string;
  bold?: boolean;
};

export const useCPTCombination = ({
  entry,
  hasNoCpt,
  messages
}: {
  entry: SmartDiaryEntry;
  hasNoCpt: boolean;
  messages: Record<string, string>;
}): { row1: RowEntry[]; row2?: RowEntry[]; primaryRow: 'row1' | 'row2' } => {
  const {
    defaultClient,
    defaultProject,
    defaultTask,
    defaultProgram
  } = useMemo(() => {
    return {
      defaultClient: hasNoCpt
        ? messages['smartDiary.noCPTClient']
        : messages['smartDiary.noClient'],
      defaultProject: hasNoCpt
        ? messages['smartDiary.noCPTProject']
        : messages['smartDiary.noProject'],
      defaultTask: hasNoCpt
        ? messages['smartDiary.noCPTTask']
        : messages['smartDiary.noTask'],
      defaultProgram: hasNoCpt
        ? messages['smartDiary.noCPTProgram']
        : messages['smartDiary.noProgram']
    };
  }, [hasNoCpt, messages]);
  const { clientEntry, projectEntry, taskEntry, programEntry } = useMemo(
    () => ({
      clientEntry: {
        text: entry.client?.value ?? defaultClient,
        id: `smart-entry-item_client_${entry.id}`
      } as RowEntry,
      projectEntry: {
        text: entry.project?.value ?? defaultProject,
        id: `smart-entry-item_project_${entry.id}`
      } as RowEntry,
      taskEntry: {
        text: entry.task?.value ?? defaultTask,
        id: `smart-entry-item_task_${entry.id}`
      } as RowEntry,
      programEntry: {
        text: entry.program?.value ?? defaultProgram,
        id: `smart-entry-item_program_${entry.id}`
      }
    }),
    [
      defaultClient,
      defaultProgram,
      defaultProject,
      defaultTask,
      entry.client?.value,
      entry.id,
      entry.program?.value,
      entry.project?.value,
      entry.task?.value
    ]
  );

  return useMemo(() => {
    switch (entry.taskNameFormat) {
      // Filter by client combination
      case DiaryTaskNameFormat.BoldTaskBreakClientProject:
        return {
          row1: [{ ...taskEntry, bold: true }],
          row2: [clientEntry, projectEntry],
          primaryRow: 'row1'
        };

      case DiaryTaskNameFormat.ClientBreakBoldProjectTask:
        return {
          row1: [clientEntry],
          row2: [{ ...projectEntry, bold: true }, taskEntry],
          primaryRow: 'row2'
        };
      case DiaryTaskNameFormat.BoldClientBreakProjectTask:
        return {
          row1: [{ ...clientEntry, bold: true }],
          row2: [projectEntry, taskEntry],
          primaryRow: 'row1'
        };
      case DiaryTaskNameFormat.ClientProjectBoldTask:
        return {
          row1: [clientEntry, projectEntry, { ...taskEntry, bold: true }],
          primaryRow: 'row1'
        };
      case DiaryTaskNameFormat.ClientProjectBreakBoldTask:
        return {
          row1: [clientEntry, projectEntry],
          row2: [{ ...taskEntry, bold: true }],
          primaryRow: 'row2'
        };
      // Filter by none
      case DiaryTaskNameFormat.ProjectBreakBoldTask:
        return {
          row1: [projectEntry],
          row2: [{ ...taskEntry, bold: true }],
          primaryRow: 'row2'
        };
      case DiaryTaskNameFormat.BoldProjectBreakTask:
        return {
          row1: [{ ...projectEntry, bold: true }],
          row2: [taskEntry],
          primaryRow: 'row1'
        };
      case DiaryTaskNameFormat.BoldTaskBreakProject:
        return {
          row1: [{ ...taskEntry, bold: true }],
          row2: [projectEntry],
          primaryRow: 'row1'
        };
      case DiaryTaskNameFormat.BoldProjectTask:
        return {
          row1: [{ ...projectEntry, bold: true }, taskEntry],
          primaryRow: 'row1'
        };
      case DiaryTaskNameFormat.ProjectTask:
        return { row1: [projectEntry, taskEntry], primaryRow: 'row1' };
      case DiaryTaskNameFormat.ProjectBoldTask:
        return {
          row1: [projectEntry, { ...taskEntry, bold: true }],
          primaryRow: 'row1'
        };

      // Filter by program combination
      case DiaryTaskNameFormat.BoldTaskBreakProgramProject:
        return {
          row1: [{ ...taskEntry, bold: true }],
          row2: [programEntry, projectEntry],
          primaryRow: 'row1'
        };
      case DiaryTaskNameFormat.ProgramBreakBoldProjectTask:
        return {
          row1: [programEntry],
          row2: [{ ...projectEntry, bold: true }, taskEntry],
          primaryRow: 'row2'
        };
      case DiaryTaskNameFormat.BoldProgramBreakProjectTask:
        return {
          row1: [{ ...programEntry, bold: true }],
          row2: [projectEntry, taskEntry],
          primaryRow: 'row1'
        };
      case DiaryTaskNameFormat.ProgramProjectBoldTask:
        return {
          row1: [programEntry, projectEntry, { ...taskEntry, bold: true }],
          primaryRow: 'row1'
        };
      case DiaryTaskNameFormat.ProgramProjectBreakBoldTask:
        return {
          row1: [programEntry, projectEntry],
          row2: [{ ...taskEntry, bold: true }],
          primaryRow: 'row2'
        };

      default:
        return entry.client
          ? {
              row1: [clientEntry, projectEntry],
              row2: [{ ...taskEntry, bold: true }],
              primaryRow: 'row2'
            }
          : {
              row1: [programEntry, projectEntry],
              row2: [{ ...taskEntry, bold: true }],
              primaryRow: 'row2'
            };
    }
  }, [
    clientEntry,
    entry.client,
    entry.taskNameFormat,
    programEntry,
    projectEntry,
    taskEntry
  ]);
};

const CustomCPTLabelFN: React.FC<CustomCPTLabelProps> = ({
  smartEntry
}: CustomCPTLabelProps) => {
  const isEntryMarkedToDelete = useIsMarkedToDelete(smartEntry);
  const hasStartEndTime = useMemo(
    () => Boolean(smartEntry?.startTime && smartEntry?.endTime),
    [smartEntry?.endTime, smartEntry?.startTime]
  );

  const classes = useStyles({
    isEntryMarkedToDelete,
    hasStartEndTime
  });
  const forceDelete = !(smartEntry.timeSignals ?? []).length;
  const messages = useMessages(labelMessages);
  const hasNoCptComb =
    !smartEntry.client?.value &&
    !smartEntry.program?.value &&
    !smartEntry.project?.value &&
    !smartEntry.task?.value;

  const { row1, row2, primaryRow } = useCPTCombination({
    entry: smartEntry,
    hasNoCpt: hasNoCptComb,
    messages
  });
  if (hasNoCptComb) {
    return (
      <div
        data-testid={`nocpt_${smartEntry.id}`}
        className={classnames(classes.root, classes.line, classes.notSelected)}
      >
        <Typography variant={'body2'}>{messages['smartDiary.no']}</Typography>
        &nbsp;
        {smartEntry.isClientConfigured && (
          <>
            <Typography
              data-testid={`smart-entry-item_client_${smartEntry.id}`}
              className={classes.singleLineText}
              variant={'body2'}
            >
              {messages['smartDiary.noCPTClient']}
            </Typography>
            <ShowSeprator hasNoCptComb smartEntryId={smartEntry.id} />
          </>
        )}
        {smartEntry.isProgramConfigured && (
          <>
            <Typography
              data-testid={`smart-entry-item_program_${smartEntry.id}`}
              className={classes.singleLineText}
              variant={'body2'}
            >
              {messages['smartDiary.noCPTProgram']}
            </Typography>
            <ShowSeprator hasNoCptComb smartEntryId={smartEntry.id} />
          </>
        )}
        <Typography
          data-testid={`smart-entry-item_project_${smartEntry.id}`}
          className={classes.singleLineText}
          variant={'body2'}
        >
          {messages['smartDiary.noCPTProject']}
        </Typography>
        <ShowSeprator hasNoCptComb smartEntryId={smartEntry.id} />
        <Typography
          data-testid={`smart-entry-item_task_${smartEntry.id}`}
          className={classes.singleLineText}
          variant={'body2'}
        >
          {messages['smartDiary.noCPTTask']}
        </Typography>
        &nbsp;
        <Typography variant={'body2'}>
          {messages['smartDiary.selected']}
        </Typography>
      </div>
    );
  }
  return (
    <div className={classes.root}>
      <div
        data-testid={`row1_${smartEntry.id}`}
        className={classnames(classes.line, {
          [classes.primaryRow]: primaryRow === 'row1',
          [classes.secondaryRow]: primaryRow !== 'row1',
          [classes.strikeThrough]: forceDelete && primaryRow === 'row1'
        })}
      >
        {row1.map((entry, index) => (
          <React.Fragment key={entry.id}>
            <Typography
              data-testid={entry.id}
              className={classnames(classes.singleLineText, {
                [classes.bold]:
                  entry.bold &&
                  smartEntry.syncStatus !== DiaryEntrySyncStatus.Synced,
                [classes.italic]:
                  smartEntry.syncStatus === DiaryEntrySyncStatus.Synced
              })}
              variant={primaryRow === 'row1' ? 'body1' : 'caption'}
            >
              {entry?.text}
            </Typography>
            {index < row1.length - 1 && (
              <ShowSeprator
                hasNoCptComb={hasNoCptComb}
                smartEntryId={smartEntry.id}
              />
            )}
          </React.Fragment>
        ))}
      </div>
      {row2 && (
        <div
          data-testid={`row2_${smartEntry.id}`}
          className={classnames(classes.line, {
            [classes.primaryRow]: primaryRow === 'row2',
            [classes.secondaryRow]: primaryRow !== 'row2',
            [classes.strikeThrough]: forceDelete && primaryRow === 'row2'
          })}
        >
          {row2.map((entry, index) => (
            <React.Fragment key={entry.id}>
              <Typography
                data-testid={entry.id}
                className={classnames(classes.singleLineText, {
                  [classes.bold]:
                    entry.bold &&
                    smartEntry.syncStatus !== DiaryEntrySyncStatus.Synced,
                  [classes.italic]:
                    smartEntry.syncStatus === DiaryEntrySyncStatus.Synced
                })}
                variant={primaryRow === 'row2' ? 'body1' : 'caption'}
              >
                {entry?.text}
              </Typography>
              {index < row2.length - 1 && (
                <ShowSeprator
                  hasNoCptComb={hasNoCptComb}
                  smartEntryId={smartEntry.id}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export const CustomCPTLabel = memo(CustomCPTLabelFN);
